import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaDownload,
  FaReceipt,
  FaSearch,
  FaTimesCircle,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import {
  downloadPhoto,
  getMyPurchases,
  getPhotographerPaymentHistory,
} from '../services/api';
import './PaymentHistory.css';

const getPhotoName = (purchase) => {
  const photo = purchase?.photo || {};
  const name = photo.name || photo.title || photo.filename || `Photo ${purchase?.photo_id || ''}`;
  return String(name).split('/').pop() || name;
};

const getEventName = (purchase) => purchase?.photo?.event?.name || 'Unassigned event';

const formatAmount = (amount) => `${Number(amount || 0).toFixed(2)} F`;

const formatDate = (date) => {
  if (!date) return 'Not available';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getPaymentLabel = (transactionId) => {
  const value = String(transactionId || '').toUpperCase();
  if (value.startsWith('WAVE-CI')) return 'Wave CI';
  if (value.startsWith('OM-CI')) return 'Orange Money CI';
  if (value.startsWith('VISA')) return 'Visa';
  if (value.startsWith('TXN')) return 'Photo purchase';
  return 'Payment';
};

const statusMeta = {
  completed: { label: 'Completed', icon: FaCheckCircle },
  pending: { label: 'Pending', icon: FaClock },
  failed: { label: 'Failed', icon: FaTimesCircle },
};

const normalizeStatus = (status) => String(status || 'pending').toLowerCase();

function PaymentHistory() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [downloadingId, setDownloadingId] = useState(null);

  const isPhotographer = user?.role === 'photographer';

  useEffect(() => {
    const loadPaymentHistory = async () => {
      setLoading(true);
      setError('');

      try {
        const response = isPhotographer
          ? await getPhotographerPaymentHistory()
          : await getMyPurchases();

        const nextRecords = isPhotographer
          ? response.sales || response.data || []
          : response.purchases || response.data || [];

        setRecords(Array.isArray(nextRecords) ? nextRecords : []);
        setSummary(response.summary || null);
      } catch (err) {
        setError(err.message || 'Failed to load payment history');
      } finally {
        setLoading(false);
      }
    };

    loadPaymentHistory();
  }, [isPhotographer]);

  const totals = useMemo(() => {
    const completed = records.filter(item => normalizeStatus(item.payment_status) === 'completed');
    return {
      count: records.length,
      completed: completed.length,
      total: completed.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    };
  }, [records]);

  const filteredRecords = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return records.filter((record) => {
      const status = normalizeStatus(record.payment_status);
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      const searchText = [
        getPhotoName(record),
        getEventName(record),
        record.transaction_id,
        record.user?.name,
        record.user?.email,
      ].filter(Boolean).join(' ').toLowerCase();

      return matchesStatus && (!normalizedQuery || searchText.includes(normalizedQuery));
    });
  }, [query, records, statusFilter]);

  const handleDownload = async (purchase) => {
    if (!purchase.photo_id) return;

    setDownloadingId(purchase.id);
    try {
      const blob = await downloadPhoto(purchase.photo_id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = getPhotoName(purchase);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="payment-history-page">
        <div className="payment-history-loading">
          <div className="spinner"></div>
          <p>Loading payment history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-history-page">
      <section className="payment-history-header">
        <div>
          <p className="payment-history-kicker">
            {isPhotographer ? 'Photographer sales' : 'My payments'}
          </p>
          <h1>Payment History</h1>
          <p>
            {isPhotographer
              ? 'Review payments received for your event photos.'
              : 'Review your purchased photos, receipts, and download access.'}
          </p>
        </div>
        <FaReceipt className="payment-history-header-icon" />
      </section>

      {error && <div className="error-message payment-history-alert">{error}</div>}

      <section className="payment-history-stats">
        <div className="payment-stat">
          <span>Total records</span>
          <strong>{summary?.total_sales ?? totals.count}</strong>
        </div>
        <div className="payment-stat">
          <span>{isPhotographer ? 'Completed sales' : 'Completed payments'}</span>
          <strong>{summary?.completed_sales ?? totals.completed}</strong>
        </div>
        <div className="payment-stat">
          <span>{isPhotographer ? 'Revenue' : 'Total spent'}</span>
          <strong>{formatAmount(summary?.total_revenue ?? totals.total)}</strong>
        </div>
      </section>

      <section className="payment-history-controls">
        <label className="payment-search">
          <FaSearch />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={isPhotographer ? 'Search buyer, event, photo, transaction' : 'Search event, photo, transaction'}
          />
        </label>

        <div className="payment-status-tabs" aria-label="Filter by status">
          {['all', 'completed', 'pending', 'failed'].map(status => (
            <button
              key={status}
              type="button"
              className={statusFilter === status ? 'active' : ''}
              onClick={() => setStatusFilter(status)}
            >
              {status === 'all' ? 'All' : statusMeta[status].label}
            </button>
          ))}
        </div>
      </section>

      {filteredRecords.length === 0 ? (
        <section className="payment-empty-state">
          <FaCreditCard />
          <h2>No payments found</h2>
          <p>
            {records.length === 0
              ? isPhotographer
                ? 'Completed customer payments for your photos will appear here.'
                : 'Purchased photos will appear here after checkout.'
              : 'Try a different search or status filter.'}
          </p>
          {!isPhotographer && records.length === 0 && (
            <Link to="/events" className="payment-empty-action">Browse Events</Link>
          )}
        </section>
      ) : (
        <section className="payment-history-table-wrap">
          <table className="payment-history-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Event</th>
                {isPhotographer && <th>Buyer</th>}
                <th>Method</th>
                <th>Transaction</th>
                <th>Date</th>
                <th>Status</th>
                <th className="amount-cell">Amount</th>
                {!isPhotographer && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((record) => {
                const status = normalizeStatus(record.payment_status);
                const StatusIcon = statusMeta[status]?.icon || FaClock;

                return (
                  <tr key={record.id}>
                    <td data-label="Photo">
                      <strong>{getPhotoName(record)}</strong>
                      <span>Photo #{record.photo_id}</span>
                    </td>
                    <td data-label="Event">{getEventName(record)}</td>
                    {isPhotographer && (
                      <td data-label="Buyer">
                        <strong>{record.user?.name || 'Customer'}</strong>
                        <span>{record.user?.email || 'No email'}</span>
                      </td>
                    )}
                    <td data-label="Method">{getPaymentLabel(record.transaction_id)}</td>
                    <td data-label="Transaction">
                      <code>{record.transaction_id || 'N/A'}</code>
                    </td>
                    <td data-label="Date">{formatDate(record.created_at)}</td>
                    <td data-label="Status">
                      <span className={`payment-status ${status}`}>
                        <StatusIcon />
                        {statusMeta[status]?.label || status}
                      </span>
                    </td>
                    <td data-label="Amount" className="amount-cell">
                      {formatAmount(record.amount)}
                    </td>
                    {!isPhotographer && (
                      <td data-label="Action">
                        <button
                          type="button"
                          className="download-receipt-button"
                          disabled={downloadingId === record.id || status !== 'completed'}
                          onClick={() => handleDownload(record)}
                        >
                          <FaDownload />
                          <span>{downloadingId === record.id ? 'Downloading' : 'Download'}</span>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

export default PaymentHistory;
